import i18n, { type BackendModule } from 'i18next';
import { initReactI18next } from 'react-i18next';
import fr from './locales/fr.json';

// Le français est embarqué (langue de repli), les autres langues sont chargées à la demande.
const lazyLocales = import.meta.glob<Record<string, unknown>>(
  ['./locales/*.json', '!./locales/fr.json'],
  { import: 'default' },
);

const lazyLocalesBackend: BackendModule = {
  type: 'backend',
  init: () => {},
  read: (lng, _ns, callback) => {
    const load = lazyLocales[`./locales/${lng}.json`];
    if (!load) return callback(new Error(`Unknown language: ${lng}`), false);
    load().then((data) => callback(null, data)).catch((error) => callback(error, false));
  },
};

// Labels volontairement non traduits : chaque langue s'affiche dans sa propre langue.
// `locale` : tag BCP 47 utilisé pour l'attribut lang et le formatage des dates (Intl).
export const SUPPORTED_LANGUAGES = [
  { code: 'fr', label: 'Français', flag: '🇫🇷', locale: 'fr-FR' },
  { code: 'es', label: 'Español', flag: '🇪🇸', locale: 'es-ES' },
  { code: 'en', label: 'English', flag: '🇬🇧', locale: 'en-GB' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹', locale: 'it-IT' },
  { code: 'pt', label: 'Português', flag: '🇵🇹', locale: 'pt-PT' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪', locale: 'de-DE' },
  { code: 'ar', label: 'العربية', flag: '🇸🇦', locale: 'ar-u-nu-latn' },
  { code: 'zh', label: '简体中文', flag: '🇨🇳', locale: 'zh-CN' },
  { code: 'nl', label: 'Nederlands', flag: '🇳🇱', locale: 'nl-NL' },
  { code: 'ro', label: 'Română', flag: '🇷🇴', locale: 'ro-RO' },
  { code: 'pl', label: 'Polski', flag: '🇵🇱', locale: 'pl-PL' },
  { code: 'sl', label: 'Slovenščina', flag: '🇸🇮', locale: 'sl-SI' },
  { code: 'bg', label: 'Български', flag: '🇧🇬', locale: 'bg-BG' },
  { code: 'ru', label: 'Русский', flag: '🇷🇺', locale: 'ru-RU' },
  { code: 'sr', label: 'Srpski', flag: '🇷🇸', locale: 'sr-Latn-RS' },
  { code: 'fi', label: 'Suomi', flag: '🇫🇮', locale: 'fi-FI' },
  { code: 'uk', label: 'Українська', flag: '🇺🇦', locale: 'uk-UA' },
  { code: 'hi', label: 'हिन्दी', flag: '🇮🇳', locale: 'hi-IN' },
  { code: 'ja', label: '日本語', flag: '🇯🇵', locale: 'ja-JP' },
  { code: 'tr', label: 'Türkçe', flag: '🇹🇷', locale: 'tr-TR' },
  { code: 'cs', label: 'Čeština', flag: '🇨🇿', locale: 'cs-CZ' },
  { code: 'ko', label: '한국어', flag: '🇰🇷', locale: 'ko-KR' },
  { code: 'el', label: 'Ελληνικά', flag: '🇬🇷', locale: 'el-GR' },
  { code: 'ca', label: 'Català', flag: '🇦🇩', locale: 'ca-ES' },
] as const;

export const isSupportedLanguage = (lang: string | null | undefined): lang is string =>
  !!lang && SUPPORTED_LANGUAGES.some(({ code }) => code === lang);

const getLanguage = (): string => {
  const stored = localStorage.getItem('language');
  if (isSupportedLanguage(stored)) return stored;

  const browserLang = navigator.language.split('-')[0];
  return isSupportedLanguage(browserLang) ? browserLang : 'fr';
};

const getLocale = (lang: string): string =>
  SUPPORTED_LANGUAGES.find(({ code }) => code === lang)?.locale ?? 'fr-FR';

const initialLanguage = getLanguage();

// Langues lues de droite à gauche : la page bascule en dir="rtl".
const RTL_LANGUAGES = ['ar'];
export const isRtlLanguage = (lang: string): boolean => RTL_LANGUAGES.includes(lang);

const applyDocumentLanguage = (lang: string) => {
  document.documentElement.lang = getLocale(lang);
  document.documentElement.dir = isRtlLanguage(lang) ? 'rtl' : 'ltr';
};

export const i18nReady = i18n
  .use(lazyLocalesBackend)
  .use(initReactI18next)
  .init({
    resources: { fr: { translation: fr } },
    partialBundledLanguages: true,
    lng: initialLanguage,
    fallbackLng: 'fr',
    ns: 'translation',
    defaultNS: 'translation',
    interpolation: {
      escapeValue: false,
    },
    keySeparator: '.',
    nsSeparator: false,
  });

applyDocumentLanguage(initialLanguage);

export const setLanguage = (lang: string) => {
  if (!isSupportedLanguage(lang)) return;
  i18n.changeLanguage(lang);
  localStorage.setItem('language', lang);
  applyDocumentLanguage(lang);
};

export const getCurrentLanguage = (): string => i18n.resolvedLanguage ?? i18n.language;

// Locale à passer à toLocaleDateString / toLocaleTimeString.
export const getCurrentLocale = (): string => getLocale(getCurrentLanguage());

export default i18n;
